import { Link } from "react-router-dom";
import { Shield, TrendingUp, Filter, Ban, Briefcase, FolderLock, ArrowRight, Sparkles, ClipboardList } from "lucide-react";

/**
 * ToolsHubPage — landing page listing all free tools for small carriers.
 *
 * Route: /tools
 *
 * Purpose: give FreightPilotAI a Spotter-style product taxonomy — 6 named
 * products, each with its own icon, tagline, and destination. Currently 2 are
 * live (BrokerCheck, RateCheck) and 4 are on the roadmap. This page positions
 * FreightPilotAI as a real product suite, not just a marketing site.
 */
export default function ToolsHubPage() {
  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Nav */}
      <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="tools-home-link">
            <span className="font-black text-xl tracking-tight">Freight</span>
            <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-xl tracking-tight">AI</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-white/70 hover:text-white transition-colors"
            data-testid="tools-back-link"
          >
            ← Home
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 30%, #FF5A1F 0%, transparent 40%), radial-gradient(circle at 75% 70%, #FF5A1F 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-14 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            The FreightPilotAI Free Toolkit
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight mb-5">
            7 free tools for
            <br />
            small carriers. <span className="text-[#FF5A1F]">All live. All free.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Enterprise SaaS companies raised $37M+ to serve 100-truck fleets. Nobody built proper tools for the guy with 3 trucks. So we did. No signup, no paywalls, no upsells — just the tools you need to run your fleet smarter today.
          </p>
        </div>
      </section>

      {/* Tools grid */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 gap-5">
          {/* LIVE TOOLS */}
          <ToolCard
            live
            to="/broker-check"
            icon={<Shield className="w-6 h-6" />}
            name="BrokerCheck"
            tagline="Verify any broker before you haul"
            body="Paste any MC# or DOT#. We pull live FMCSA data and give you a SAFE/CAUTION/AVOID verdict in 2 seconds. 6-point verification checklist included."
            cta="Open BrokerCheck"
            testid="tools-card-brokercheck"
          />

          <ToolCard
            live
            to="/rate-check"
            icon={<TrendingUp className="w-6 h-6" />}
            name="RateCheck"
            tagline="Never take a lowball rate again"
            body="Enter your lane. Get the fair market rate range instantly. Add the broker's offer to see a 5-tier verdict: AVOID / LOWBALL / FAIR / GENEROUS / TOO GOOD to be true."
            cta="Open RateCheck"
            testid="tools-card-ratecheck"
          />

          {/* LoadFilter — Chrome extension download */}
          <ToolCard
            live
            href="/loadfilter-extension.zip"
            download="loadfilter-extension.zip"
            icon={<Filter className="w-6 h-6" />}
            name="LoadFilter"
            tagline="Chrome extension for verified load boards"
            body="Browse DAT, Truckstop, or TruckerPath and get an on-screen flag on any broker in the Broker Directory. Free, no account required. 30-second install."
            cta="Download extension"
            testid="tools-card-loadfilter"
          />

          <ToolCard
            live
            to="/dnp"
            icon={<Ban className="w-6 h-6" />}
            name="Broker Directory"
            tagline="Community-verified broker payment reputation"
            body="Look up any broker's payment history before you haul. Community-shared reports on brokers who ghost, dispute invoices, pay 70+ days late, or double-broker. Check first, haul confident."
            cta="Open Broker Directory"
            testid="tools-card-dnp"
          />

          <ToolCard
            live
            to="/authority-checklist"
            icon={<ClipboardList className="w-6 h-6" />}
            name="Authority Checklist"
            tagline="MC# to first load, in order"
            body="Just applied for your MC? Every filing, permit and account a new carrier needs — BOC-3, UCR, IRP, IFTA, Clearinghouse, DAT — sequenced day by day with costs, timing and official links. Tick it off from your phone."
            cta="Open the checklist"
            testid="tools-card-authority"
          />

          <ToolCard
            live
            to="/careers-builder"
            icon={<Briefcase className="w-6 h-6" />}
            name="Careers Page Builder"
            tagline="A free driver-hiring page for your fleet"
            body="Build a professional 'We're Hiring Drivers' page for your company in 5 minutes. Hosted at freightpilotai.ai/jobs/yourfleet. Application form built in. Free forever."
            cta="Build my page"
            testid="tools-card-careers"
          />

          <ToolCard
            live
            to="/app/vault"
            icon={<FolderLock className="w-6 h-6" />}
            name="Docs Vault"
            tagline="Never lose an insurance certificate again"
            body="Private per-carrier vault for COI, W-9, MC authority, IFTA quarterly, driver files, and more. 10 MB per file, tag + search, download any time. Requires a free account."
            cta="Open Docs Vault"
            testid="tools-card-docs"
          />
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-white/10 bg-neutral-900/50">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3">
            Want us to handle the whole thing?
          </div>
          <h2 className="text-3xl sm:text-4xl font-black mb-4 tracking-tight">
            Free tools are the appetizer. Concierge Setup is the meal.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
            These 6 tools give you what enterprise SaaS charges $500/month for. But if you'd rather someone just <b className="text-white">set up your whole operation for you</b> — carrier authority, load board accounts, DNP screening, careers page, ELD hookup — we do that too. Free for your first 7 days. Then you decide.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-6 py-3 transition-colors"
            data-testid="tools-cta-signup"
          >
            Set It Up For Me — Free
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-white/50">
          <div>
            © 2026 <b className="text-white">FreightPilotAI</b> · The free toolkit for small carriers
          </div>
          <div className="flex items-center gap-4">
            <Link to="/broker-check" className="hover:text-white">BrokerCheck</Link>
            <Link to="/authority-checklist" className="hover:text-white">Authority Checklist</Link>
            <Link to="/rate-check" className="hover:text-white">RateCheck</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ─────────────────── Tool card ─────────────────── */
function ToolCard({ live, comingSoon, to, href, download, icon, name, tagline, body, cta, testid }) {
  const inner = (
    <div
      className={`h-full rounded-2xl border-2 p-6 sm:p-7 transition-all ${
        live
          ? "border-white/15 bg-white/5 hover:border-[#FF5A1F]/60 hover:bg-white/[0.07] cursor-pointer group"
          : "border-white/10 bg-white/[0.02] opacity-70"
      }`}
      data-testid={testid}
    >
      {/* Status pill */}
      <div className="flex items-center justify-between mb-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
          live ? "bg-[#FF5A1F]/20 text-[#FF5A1F]" : "bg-white/5 text-white/40"
        }`}>
          {icon}
        </div>
        {live && (
          <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold border border-emerald-500/30 bg-emerald-500/10 rounded-full px-2.5 py-1">
            Live now
          </span>
        )}
        {comingSoon && (
          <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold border border-white/10 bg-white/5 rounded-full px-2.5 py-1">
            Coming soon
          </span>
        )}
      </div>

      {/* Name + tagline */}
      <div className="mb-3">
        <h3 className={`text-xl sm:text-2xl font-black tracking-tight mb-1 ${live ? "text-white" : "text-white/70"}`}>
          {name}
        </h3>
        <p className={`text-sm font-semibold ${live ? "text-[#FF5A1F]" : "text-white/40"}`}>
          {tagline}
        </p>
      </div>

      {/* Body */}
      <p className={`text-sm leading-relaxed mb-5 ${live ? "text-white/70" : "text-white/40"}`}>
        {body}
      </p>

      {/* CTA */}
      {live && (
        <div className="flex items-center gap-2 text-sm font-bold text-[#FF5A1F] group-hover:gap-3 transition-all">
          {cta}
          <ArrowRight className="w-4 h-4" />
        </div>
      )}
      {comingSoon && (
        <div className="text-sm text-white/40">
          On the roadmap — customers get early access
        </div>
      )}
    </div>
  );

  if (live && href) {
    return <a href={href} download={download} data-testid={`${testid}-link`}>{inner}</a>;
  }
  if (live && to) {
    return <Link to={to}>{inner}</Link>;
  }
  return inner;
}
